/** @jsx jsx */
import React, { useCallback, useState, type KeyboardEvent } from 'react';
import { css, jsx } from '@compiled/react';
import Heading from '@atlaskit/heading';
import InlineDialog from '@atlaskit/inline-dialog';
import InlineEdit from '@atlaskit/inline-edit';
import { token } from '@atlaskit/tokens';
import TextArea from '@atlassian/jira-common-components-text-area/src/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useDefaultAttributeValue } from '@atlassian/jira-servicedesk-insight-object-attribute-utils/src/utils/default/index.tsx';
import { useLabelAttribute } from '@atlassian/jira-servicedesk-insight-object-details-store/src/controllers/store/index.tsx';
import { useEditableAttribute } from '@atlassian/jira-servicedesk-insight-object-details-store/src/controllers/use-editable-attribute/index.tsx';
import { getFirstValidationErrorMessage } from '@atlassian/jira-servicedesk-insight-shared-utils/src/common/utils/get-validation-error/index.tsx';
import { useStringLengthValidation } from '@atlassian/jira-servicedesk-insight-shared-utils/src/common/utils/string-length-validation/index.tsx';
import messages from './messages.tsx';

export type Props = {
	canEdit: boolean;
};

export const ObjectNameInlineEdit = (props: Props) => {
	const { formatMessage } = useIntl();
	const { canEdit } = props;
	const [labelAttribute] = useLabelAttribute();
	const [, { update }] = useEditableAttribute(labelAttribute.objectTypeAttribute);
	const [value, setValue] = useDefaultAttributeValue(labelAttribute);
	const { handleEmptyStringValidation } = useStringLengthValidation();
	const [isEditing, setIsEditing] = useState(false);
	const [serverError, setServerError] = useState<string | null>(null);
	const [originalValue, setOriginalValue] = useState(value);

	const onConfirm = useCallback(
		(updatedLabel: string) => {
			const updated = updatedLabel.trim();
			setValue(updated);
			setServerError(null);
			if (updated !== originalValue) {
				update({
					updatedValues: [{ value: updated }],
					onError: (error) => {
						setIsEditing(true);
						const errorMessage =
							getFirstValidationErrorMessage(error) ?? formatMessage(messages.unknownError);
						setServerError(errorMessage);
					},
				});
			}
			setIsEditing(false);
		},
		[formatMessage, originalValue, setValue, update],
	);

	const onEdit = () => {
		setServerError(null);
		setOriginalValue(value);
		setIsEditing(true);
	};

	const onCancel = () => {
		setServerError(null);
		setValue(originalValue);
		setIsEditing(false);
	};

	const createOnKeyDown = useCallback(
		(currentValue: string | null) => (event: KeyboardEvent<HTMLElement>) => {
			// Prevents 'enter' from inserting a newline character, and instead
			// 'submits' the inline edit. Note that this could be achieved ootb
			// with an atlaskit TextField (instead of TextArea), however there
			// are stricter styling contraints on that component which makes
			// matching the design spec impossible.
			if (event.key === 'Enter') {
				event.preventDefault();
				if (currentValue != null && currentValue !== '') {
					onConfirm(currentValue);
				} else {
					setIsEditing(false);
				}
			}
		},
		[onConfirm],
	);

	if (canEdit === false) {
		return (
			<div css={[readViewStyle]}>
				<Heading size="large" as="h1">
					{value}
				</Heading>
			</div>
		);
	}

	return (
		<InlineEdit
			isEditing={isEditing}
			onEdit={onEdit}
			onConfirm={onConfirm}
			onCancel={onCancel}
			editButtonLabel={formatMessage(messages.editButtonLabel)}
			defaultValue={value}
			readViewFitContainerWidth
			readView={() => {
				return (
					<div css={[readViewStyle]}>
						<Heading
							size="large"
							as="h1"
							testId="servicedesk-insight-object-view.ui.header.name-inline-edit.page-title"
						>
							{value}
						</Heading>
					</div>
				);
			}}
			editView={({ errorMessage, ...fieldProps }) => {
				const error = serverError ?? errorMessage;
				const isInvalid = error !== undefined;
				return (
					<InlineDialog content={error} isOpen={error !== undefined} placement="right">
						<div css={[editViewContainerStyle, isInvalid && editViewContainerInvalidBorderStyle]}>
							<Heading size="large" as="h1">
								<TextArea
									{...fieldProps}
									isRequired
									shouldAutoFocus
									onKeyDown={createOnKeyDown(fieldProps.value)}
									aria-label={formatMessage(messages.inlineEditLabel)}
								/>
							</Heading>
						</div>
					</InlineDialog>
				);
			}}
			validate={handleEmptyStringValidation(formatMessage(messages.objectLabel))}
		/>
	);
};

const readViewStyle = css({
	marginTop: 0,
	paddingTop: token('space.075', '6px'),
	paddingRight: token('space.075', '6px'),
	paddingBottom: token('space.075', '6px'),
	paddingLeft: token('space.075', '6px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	h1: {
		textOverflow: 'ellipsis',
		overflow: 'hidden',
	},
});

const editViewContainerStyle = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	h1: {
		marginTop: '0',
		paddingTop: token('space.075', '6px'),
		paddingRight: token('space.075', '6px'),
		paddingBottom: token('space.075', '6px'),
		paddingLeft: token('space.075', '6px'),
		wordBreak: 'break-word',
		transition: 'background 0.2s',
		borderStyle: 'solid',
		borderRadius: '3px',
		borderWidth: '2px',
		boxSizing: 'border-box',
		overflow: 'auto',
		borderColor: `${token('color.border.brand')}`,
	},
});

const editViewContainerInvalidBorderStyle = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	h1: { borderColor: `${token('color.border.danger')}` },
});
